import React, { useEffect, useRef, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { CallWithAuth } from "../../action/apiActions";
import {
  GET_PATIENT_RELATIVES_API_URL,
  UPLOAD_PATIENT_REPORT_RECORD_SESSION,
  UPLOAD_CLICNICAL_INTAKE_DOC,
  UPLOAD_PATIENT_REPORT_RECORD_SESSION_SAVE,
} from "../../action/apiPath";
import { PiFileAudioFill, PiFilePdf } from "react-icons/pi";
import { showToast } from "../../util/toastHelper";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";

const MySwal = withReactContent(Swal);

function UploadReportModal({ show, onHide, patientid, onSuccess }) {
  const [fileType, setFileType] = useState("");
  const [selectedSession, setSelectedSession] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadedFileInfo, setUploadedFileInfo] = useState([]);
  const [uploadedRecordSeesion, setPatientUploadedRecordSession] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false); // for save button
  const [uploading, setUploading] = useState(false); // for file upload loader
  const docRef = useRef(null);
  const audioRef = useRef(null);
  const docRef2 = useRef(null);

  const fetchPatientRecordSession = async () => {
    setLoading(true);
    try {
      const url = `${GET_PATIENT_RELATIVES_API_URL}/${patientid}/?extra=true`;
      const response = await CallWithAuth("GET", url);

      if (response?.res?.data?.statusCode === 200) {
        setPatientUploadedRecordSession(
          response?.res?.data?.data?.record_sessions || []
        );
        setAssessments(
          response?.res?.data?.data?.assessments || []
        );
      } else {
        setPatientUploadedRecordSession([]);
        setAssessments([]);
      }
    } catch (error) {
      console.error("Error fetching record sessions:", error);
      setPatientUploadedRecordSession([]);
      setAssessments([]);
    } finally {
      setLoading(false);
    }
  };

  // ==============================
  // Page Loading time API call
  // ==============================
  useEffect(() => {
    fetchPatientRecordSession();
  }, [patientid]);

  // ======================
  // File Upload API Call
  // ======================
  const apiCallFileUpload = async (files, type, inputId) => {
    setUploadedFiles([]);
    setUploadedFileInfo([]);
    try {
      setErrorMsg("");
      setUploading(true);

      const uploadedUrls = [];
      const _uploadedFileInfo = [];

      for (const file of files) {
        const formdata = new FormData();
        formdata.append("file", file);

        const apiEndpoint =
          selectedSession === "assessment"
            ? UPLOAD_CLICNICAL_INTAKE_DOC
            : UPLOAD_PATIENT_REPORT_RECORD_SESSION;

        const res = await CallWithAuth("POST", apiEndpoint, formdata);

        if (res?.res?.data?.data?.file_url) {
          uploadedUrls.push(res.res.data.data.file_url);
          _uploadedFileInfo.push({
            file_name: file.name,
            file_size: file.size
          });
        }
        else if (res?.res?.data?.error) {
          setErrorMsg(res?.res?.data?.message);
        }
      }

      setUploadedFiles((prev) => [...prev, ...uploadedUrls]);
      setUploadedFileInfo((prev) => [...prev, ..._uploadedFileInfo]);
      setSelectedFiles(files.map((f) => f.name));
    } catch (err) {
      console.error("Upload error:", err);
      setErrorMsg("File upload failed!");
    } finally {
      setUploading(false);
    }
    document.getElementById(inputId).value = '';
  };

  const getValidExtensions = () => {
    if (selectedSession === "assessment") {
      return /\.(pdf|doc|docx)$/i;
    }
    if (fileType === "audio") return /\.(mp3|m4a)$/i;
    if (fileType === "pdf") return /\.pdf$/i;
    return null;
  };

  const handleFileChange = (event, type) => {
    if (!fileType && selectedSession !== "assessment") {
      setErrorMsg("Please select file type first!");
      event.target.value = "";
      return;
    }

    let files = Array.from(event.target.files);
    if (files.length === 0) return;

    const validExtensions = getValidExtensions();
    const invalidFiles = files.filter((file) => !validExtensions.test(file.name));

    if (invalidFiles.length > 0) {
      setErrorMsg(
        `Invalid file type! Please upload only ${
          selectedSession === "assessment"
            ? "PDF/DOC/DOCX"
            : fileType === "audio"
            ? "MP3/M4A"
            : "PDF"
        } file${selectedSession === "assessment" ? "s" : ""}.`
      );
      event.target.value = "";
      return;
    }

    apiCallFileUpload(files, type, event.target.id);
  };

  const handleDrop = (e, type) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    setFileType(type); // ✅ drag করলে fileType auto set হবে
    setErrorMsg("");
    setSelectedFiles([]);
    handleFileChange({ target: { files } }, type);
  };

  // ======================
  // Submit Handler
  // ======================
  const handleSubmitAssesment = async () => {
    let payload = {};

    if (selectedSession === "assessment") {
      // ✅ Assessment = Array of objects
      payload = {
        assessments: uploadedFiles.map((fileUrl, i) => ({
          assessment_id: "",
          report_url: fileUrl,
          file_name: uploadedFileInfo[i].file_name,
          file_size: uploadedFileInfo[i].file_size
        })),
      };
    } else {
      // ✅ Others = Single file only
      // payload[selectedSession] = uploadedFiles;  // for array structure
      payload[selectedSession] = uploadedFiles[0];
      payload.file_name = uploadedFileInfo[0].file_name;
      payload.file_size = uploadedFileInfo[0].file_size;
    }

    try {
      setIsUploading(true);
      setErrorMsg("");
      const response = await CallWithAuth(
        "POST",
        UPLOAD_PATIENT_REPORT_RECORD_SESSION_SAVE + `/${patientid}`,
        payload
      );

      if (response?.res?.status === 201) {
        onSuccess({
          fileType,
          selectedSession,
          files: uploadedFiles,
        });
        onHide();
      } else {
        setErrorMsg("Failed to save file session.");
      }
    } catch (error) {
      console.error("Error while saving:", error);
      setErrorMsg("Error occurred while saving file session.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!uploadedFiles.length || !selectedSession) {
      setErrorMsg("Please choose session type and upload a file.");
      return;
    }

    if (selectedSession === "assessment") {
      handleSubmitAssesment();
      return;
    }

    try {
      setIsUploading(true);
      setErrorMsg("");
      let i = 0;
      let _uploadedFiles = [];
      for (let uploadedFile of uploadedFiles) {
        const response = await CallWithAuth(
          "POST",
          UPLOAD_PATIENT_REPORT_RECORD_SESSION_SAVE + `/${patientid}`,
          {
            [selectedSession]: uploadedFile,
            ...uploadedFileInfo[i]
          }
        );
  
        if (response?.res?.status === 201) {
          _uploadedFiles.push(selectedSession);
        } else {
          setErrorMsg(`File ${i + 1}: Failed to save file session.`);
        }
        i++;
      }

      if (_uploadedFiles.length == uploadedFiles.length) {
        onSuccess({
          fileType,
          selectedSession,
          files: uploadedFiles,
        });
        onHide();
      } else {
        setUploadedFiles(_uploadedFiles);
        setUploadedFileInfo([]);
      }
    } catch (error) {
      console.error("Error while saving:", error);
      setErrorMsg("Error occurred while saving file session.");
    } finally {
      setIsUploading(false);
    }
  };

  const hasExistingRecord = (type) => {
    if (type == 'assessment') {
      return assessments?.some(
        (item) =>
          item.transcription_text &&
          item.transcription_text !== ""
      );
    }

    return uploadedRecordSeesion?.some(
      (item) =>
        item.session_type === type &&
        item.transcription_text &&
        item.transcription_text !== ""
    );
  };

  const hasSessionFiles = (type) => {
    if (type == 'assessment') {
      return !!assessments?.filter(item => item.assessment_report)?.length;
    }

    return !!uploadedRecordSeesion?.filter(item => item.file && item.session_type === type)?.length;
  };

  const handleDeleteFile = async (item, type) => {
    const result = await MySwal.fire({
      title: "Are you sure want to delete this file?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    });
    if (result.isConfirmed) {
        setLoading(true);
        try {
            const response = await CallWithAuth("DELETE", `${UPLOAD_PATIENT_REPORT_RECORD_SESSION_SAVE}/${item.id}?report_type=${type}`);
            if (
                response?.res?.data?.statusCode === 200 ||
                response?.res?.data?.statusCode === 201
            ) {
                showToast("success", "File has been deleted successfully");
                fetchPatientRecordSession();
            } else {
                showToast("error", response?.res?.data?.message);
            }
        } catch (error) {
            showToast("error", "Something went wrong while deleting social media.");
            setLoading(false);
        }
    }
  };

  const renderAssessments = () => {
    const _files = assessments?.filter(item => item.assessment_report);
    
    if (!_files?.length) return null;
  
    return (
      <div className="file-list-container row g-3 mt-1">
        {_files.map((item, i) => {
          const parts = item.assessment_report.split("/");
          const name = parts[parts.length - 1];
          const extension = name.split(".").pop().toLowerCase();
          const filename = item.file_name || name;
          const isPdf = extension === 'pdf';
  
          return (
            <div key={i} className="col-sm-6">
              <div className="file-item p-3 border rounded d-flex align-items-center bg-white shadow-sm h-100 position-relative">
                
                {/* Status & Action Icons Container */}
                <div className="position-absolute d-flex align-items-center gap-2" style={{ top: '8px', right: '10px' }}>
                  {/* Success Checkmark */}
                  {item.transcription_text && item.transcription_text !== '' && (
                    <span className="text-success">
                      <svg width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                      </svg>
                    </span>
                  )}
  
                  {/* DELETE ICON */}
                  <button 
                    onClick={() => handleDeleteFile(item, 'assessment')}
                    className="btn btn-link p-0 text-danger border-0"
                    title="Delete File"
                    style={{ lineHeight: 0 }}
                  >
                    <svg width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z"/>
                      <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z"/>
                    </svg>
                  </button>
                </div>
  
                <div className="d-flex align-items-center gap-3 w-100 mt-1">
                  {/* Dynamic Icon Section */}
                  <div className={`icon-circle p-2 rounded-circle ${isPdf ? 'bg-success-subtle' : 'bg-primary-subtle'}`}>
                    {isPdf ? (
                      <svg width="20" height="20" fill="currentColor" className="text-success" viewBox="0 0 16 16">
                        <path d="M5 4a.5.5 0 0 0 0 1h6a.5.5 0 0 0 0-1H5zm0 2a.5.5 0 0 0 0 1h6a.5.5 0 0 0 0-1H5zm0 2a.5.5 0 0 0 0 1h3a.5.5 0 0 0 0-1H5z"/><path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2zm10-1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1z"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" fill="currentColor" className="text-primary" viewBox="0 0 16 16">
                        <path d="M3.5 6.5A.5.5 0 0 1 4 7v1a4 4 0 0 0 8 0V7a.5.5 0 0 1 1 0v1a5 5 0 0 1-4.5 4.975V15h3a.5.5 0 0 1 0 1h-7a.5.5 0 0 1 0-1h3v-2.025A5 5 0 0 1 3 8V7a.5.5 0 0 1 .5-.5z"/><path d="M10 8a2 2 0 1 1-4 0V3a2 2 0 1 1 4 0v5z"/>
                      </svg>
                    )}
                  </div>
  
                  {/* Text Section */}
                  <div className="flex-grow-1 overflow-hidden pe-4">
                    <a 
                      className="text-truncate d-block fw-medium text-decoration-none text-primary" 
                      style={{ maxWidth: "100%" }} 
                      href={item.assessment_report} 
                      target="_blank" 
                      rel="noreferrer"
                      title={filename}
                    >
                      {filename}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderSessionFiles = (type) => {
    if (type == "assessment") {
      return renderAssessments();
    }
  
    const _files = uploadedRecordSeesion?.filter(item => (item.file || item.speech_url) && item.session_type === type);
    
    if (!_files?.length) return null;
  
    return (
      <div className="file-list-container row g-3 mt-1">
        {_files.map((item, i) => {
          let isPdf = false;
          let filename = '';
          if (item.file) {
            const parts = item.file.split("/");
            const name = parts[parts.length - 1];
            const extension = name.split(".").pop().toLowerCase();
            isPdf = extension === 'pdf';
            filename = item.file_name || name;
          } else if (item.speech_url) {
            filename = 'Audio Recording';
          }
  
          return (
            <div key={i} className="col-sm-6">
              <div className="file-item p-3 border rounded d-flex align-items-center bg-white shadow-sm h-100 position-relative">
                
                {/* Status & Action Icons Container */}
                <div className="position-absolute d-flex align-items-center gap-2" style={{ top: '8px', right: '10px' }}>
                  {/* Success Checkmark */}
                  {item.transcription_text && item.transcription_text !== '' && (
                    <span className="text-success">
                      <svg width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                      </svg>
                    </span>
                  )}
  
                  {/* DELETE ICON */}
                  <button 
                    onClick={() => handleDeleteFile(item, type)}
                    className="btn btn-link p-0 text-danger border-0"
                    title="Delete File"
                    style={{ lineHeight: 0 }}
                  >
                    <svg width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z"/>
                      <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z"/>
                    </svg>
                  </button>
                </div>
  
                <div className="d-flex align-items-center gap-3 w-100 mt-1">
                  {/* Dynamic Icon Section */}
                  <div className={`icon-circle p-2 rounded-circle ${isPdf ? 'bg-success-subtle' : 'bg-primary-subtle'}`}>
                    {isPdf ? (
                      <svg width="20" height="20" fill="currentColor" className="text-success" viewBox="0 0 16 16">
                        <path d="M5 4a.5.5 0 0 0 0 1h6a.5.5 0 0 0 0-1H5zm0 2a.5.5 0 0 0 0 1h6a.5.5 0 0 0 0-1H5zm0 2a.5.5 0 0 0 0 1h3a.5.5 0 0 0 0-1H5z"/><path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2zm10-1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1z"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" fill="currentColor" className="text-primary" viewBox="0 0 16 16">
                        <path d="M3.5 6.5A.5.5 0 0 1 4 7v1a4 4 0 0 0 8 0V7a.5.5 0 0 1 1 0v1a5 5 0 0 1-4.5 4.975V15h3a.5.5 0 0 1 0 1h-7a.5.5 0 0 1 0-1h3v-2.025A5 5 0 0 1 3 8V7a.5.5 0 0 1 .5-.5z"/><path d="M10 8a2 2 0 1 1-4 0V3a2 2 0 1 1 4 0v5z"/>
                      </svg>
                    )}
                  </div>
  
                  {/* Text Section */}
                  <div className="flex-grow-1 overflow-hidden pe-4">
                    <a 
                      className="text-truncate d-block fw-medium text-decoration-none text-primary" 
                      style={{ maxWidth: "100%" }} 
                      href={item.file} 
                      target="_blank" 
                      rel="noreferrer"
                      title={filename}
                    >
                      {filename}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Upload Report File</Modal.Title>
      </Modal.Header>

      {loading ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "300px" }}
        >
          <Spinner animation="border" />
          <span className="ms-2">Loading patient record...</span>
        </div>
      ) : (
        <>
          <Modal.Body>
            {/* Step 1: Choose Session */}
            <div className="mb-3">
              <label className="fw-normal mb-2">Choose Session:</label>
              <select
                className="form-select"
                value={selectedSession}
                onChange={(e) => {
                  setSelectedSession(e.target.value);
                  setFileType("");
                  setSelectedFiles([]);
                  setErrorMsg("");
                }}
              >
                <option value="">-- Select --</option>
                <option value="backgroundInfo">Background Information</option>
                <option value="clinical_interview">Clinical Interview</option>
                <option value="formulation">Formulation</option>
                <option value="assessment">Assessment</option>
                <option value="mse">MSE</option>
              </select>
            </div>

            {/* Step 2: File Type + Upload UI */}
            {selectedSession !== "assessment" && (
              <div className="mb-3 d-flex gap-3">
                {/* Audio Card */}
                <div
                  className={`option-boxContent-card p-3 border rounded ${
                    fileType === "audio" ? "border-primary" : ""
                  }`}
                  onClick={() => {
                    setFileType("audio");
                    setErrorMsg("");
                    setSelectedFiles([]);
                    document.getElementById("audioFileInput").click();
                  }}
                  style={{ cursor: "pointer", flex: 1 }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.add("drag-over");
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.classList.remove("drag-over");
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove("drag-over");
                    handleDrop(e, "audio");
                  }}
                >
                  <figure className="text-center">
                    <div className="icon">
                      <PiFileAudioFill size={28} />
                    </div>
                    <figcaption>Audio Recording</figcaption>
                  </figure>
                  <h6>Supported: MP3 / M4A</h6>
                  <p>Drag & Drop or Browse to upload</p>

                  {/* Uploading Loader */}
                  {uploading && fileType === "audio" ? (
                    <div className="text-center mt-2">
                      <Spinner animation="border" size="sm" /> Uploading...
                    </div>
                  ) : (
                    selectedFiles.length > 0 &&
                    fileType === "audio" && (
                      <ul className="mt-2">
                        {selectedFiles.map((name, idx) => (
                          <li key={idx}>{name}</li>
                        ))}
                      </ul>
                    )
                  )}

                  <input
                    id="audioFileInput"
                    type="file"
                    accept=".mp3,.m4a"
                    multiple={['backgroundInfo', 'clinical_interview'].includes(selectedSession)}
                    style={{ display: "none" }}
                    onChange={(e) => handleFileChange(e, "audio")}
                  />
                </div>

                {/* PDF Card */}
                <div
                  className={`option-boxContent-card p-3 border rounded ${
                    fileType === "pdf" ? "border-primary" : ""
                  }`}
                  onClick={() => {
                    setFileType("pdf");
                    setErrorMsg("");
                    setSelectedFiles([]);
                    document.getElementById("pdfFileInput").click();
                  }}
                  style={{ cursor: "pointer", flex: 1 }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.add("drag-over");
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.classList.remove("drag-over");
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove("drag-over");
                    handleDrop(e, "pdf");
                  }}
                >
                  <figure className="text-center">
                    <div className="icon">
                      <PiFilePdf size={28} />
                    </div>
                    <figcaption>PDF</figcaption>
                  </figure>
                  <h6>Supported: PDF</h6>
                  <p>Drag & Drop or Browse to upload</p>

                  {/* Uploading Loader */}
                  {uploading && fileType === "pdf" ? (
                    <div className="text-center mt-2">
                      <Spinner animation="border" size="sm" /> Uploading...
                    </div>
                  ) : (
                    selectedFiles.length > 0 &&
                    fileType === "pdf" && (
                      <ul className="mt-2">
                        {selectedFiles.map((name, idx) => (
                          <li key={idx}>{name}</li>
                        ))}
                      </ul>
                    )
                  )}

                  <input
                    id="pdfFileInput"
                    type="file"
                    accept=".pdf"
                    multiple={['backgroundInfo', 'clinical_interview'].includes(selectedSession)}
                    style={{ display: "none" }}
                    onChange={(e) => handleFileChange(e, "pdf")}
                  />
                </div>
              </div>
            )}

            {/* Assessment Card */}
            {selectedSession === "assessment" && (
              <div
                className="option-boxContent-card p-3 border rounded"
                onClick={() => document.getElementById("assessmentFileInput").click()}
                style={{ cursor: "pointer" }}
                onDragOver={(e) => {
                e.preventDefault();
                e.currentTarget.classList.add("drag-over");
              }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove("drag-over");
                  handleDrop(e, "document")}}
              >
                <figure className="text-center">
                  <div className="icon">
                    <PiFilePdf size={28} />
                  </div>
                  <figcaption>Assessment Document</figcaption>
                </figure>
                <h6>Supported: PDF / DOC / DOCX</h6>
                <p>Drag & Drop or Browse to upload</p>
                <p>You can upload files with a maximum size of 25MB.</p>

                {/* Uploading Loader */}
                {uploading ? (
                  <div className="text-center mt-2">
                    <Spinner animation="border" size="sm" /> Uploading...
                  </div>
                ) : (
                  selectedFiles.length > 0 && (
                    <ul className="mt-2">
                      {selectedFiles.map((name, idx) => (
                        <li key={idx}>{name}</li>
                      ))}
                    </ul>
                  )
                )}

                <input
                  id="assessmentFileInput"
                  type="file"
                  accept=".pdf,.doc,.docx"
                  multiple
                  style={{ display: "none" }}
                  onChange={(e) => handleFileChange(e, "document")}
                />
              </div>
            )}

            {/* Existing Records */}
            {hasExistingRecord("clinical_interview") && (<>
              <p className="sm_txt mt-2 text-success">
                A Clinical Interview has already been uploaded.
              </p>
            </>)}
            {!hasExistingRecord("clinical_interview") && hasSessionFiles("clinical_interview") && (<>
              <p className="sm_txt mt-2 text-success">
                Clinical Interview
              </p>
            </>)}
            {renderSessionFiles("clinical_interview")}
            {(hasExistingRecord("clinical_interview") || hasSessionFiles("clinical_interview")) && <hr/>}

            {hasExistingRecord("formulation") && (<>
              <p className="sm_txt mt-2 text-success">
                A Formulation has already been uploaded.
              </p>
            </>)}
            {!hasExistingRecord("formulation") && hasSessionFiles("formulation") && (<>
              <p className="sm_txt mt-2 text-success">
                Formulation
              </p>
            </>)}
            {renderSessionFiles("formulation")}
            {(hasExistingRecord("formulation") || hasSessionFiles("formulation")) && <hr/>}

            {hasExistingRecord("assessment") && (<>
              <p className="sm_txt mt-2 text-success">
                An Assessment has already been uploaded.
              </p>
            </>)}
            {!hasExistingRecord("assessment") && hasSessionFiles("assessment") && (<>
              <p className="sm_txt mt-2 text-success">
                Assessment
              </p>
            </>)}
            {renderSessionFiles("assessment")}
            {(hasExistingRecord("assessment") || hasSessionFiles("assessment")) && <hr/>}

            {hasExistingRecord("mse") && (<>
              <p className="sm_txt mt-2 text-success">
                An MSE has already been uploaded.
              </p>
            </>)}
            {!hasExistingRecord("mse") && hasSessionFiles("mse") && (<>
              <p className="sm_txt mt-2 text-success">
                MSE
              </p>
            </>)}
            {renderSessionFiles("mse")}
            {(hasExistingRecord("mse") || hasSessionFiles("mse")) && <hr/>}

            {(hasExistingRecord("background_info_audio") || hasExistingRecord("background_info_pdf")) && (<>
              <p className="sm_txt mt-2 text-success">
                A Background Information has already been uploaded.
              </p>
            </>)}
            {!hasExistingRecord("background_info_audio") && !hasExistingRecord("background_info_pdf") && (hasSessionFiles("background_info_audio") || hasSessionFiles("background_info_pdf")) && (<>
              <p className="sm_txt mt-2 text-success">
                Background Info
              </p>
            </>)}
            {renderSessionFiles("background_info_audio")}
            {renderSessionFiles("background_info_pdf")}
            {(hasExistingRecord("background_info_audio") || hasExistingRecord("background_info_pdf") || hasSessionFiles("background_info_audio") || hasSessionFiles("background_info_pdf")) && <hr/>}

            {errorMsg && (
              <p className="mt-2 mb-0 text-danger fw-bold">{errorMsg}</p>
            )}
          </Modal.Body>

          <Modal.Footer>
            <button
              className="btn btn-secondary"
              onClick={onHide}
              disabled={isUploading}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={isUploading}
            >
              {isUploading ? (
                <span>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Uploading...
                </span>
              ) : (
                "Upload"
              )}
            </button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
}

export default UploadReportModal;
